import {ReactElement, useState} from 'react';
import React from 'react';
import type {CalendarDateTimePickerProps} from '@Components/calendar-date-time-picker';
import {CalendarDateTimePicker} from '@Components/calendar-date-time-picker';
import {renderElement} from '@Utils/root-element.util';
import {Provider} from 'react-redux';
import {Button, IconPlacement, Size, Type} from '@Components/button';
import {noop} from '@Utils/general.util';
import {publishEmailCampaignToSocialMedia} from '@Libraries/email-campaigns-library';
import {openAddMailingListModal, PANEL_ID} from '@Modals/add-mailing-list-modal';
import {AddMailingListStep} from '@Panels/add-mailing-list-panel/add-mailing-list-panel.types';
import type {EmailMakerSuccessCtaContainerProps} from '@Components/email-maker-success-cta-container/email-maker-success-cta-container';
import EmailMakerSuccessCtaContainer from '@Components/email-maker-success-cta-container/email-maker-success-cta-container';
import {EmailMakerPublishingOptionsPopup} from '@Components/email-maker-publishing-options-popup';
import {IconShape, IconSize, IconType} from '@Components/icon-v2/icon.types';
import {Icon} from '@Components/icon-v2';
import {ALIGNMENT_TYPE} from '@Components/dropdown-v2/dropdown.types';
import {openEmailCampaignSendingOptionsModal} from '@Modals/email-campaign-sending-options-modal';
import type {EmailCampaignSendingOptionsPanelTypes} from '@Panels/email-campaign-sending-options-panel/email-campaign-sending-options-panel.types';
import type {EmailCampaign} from '@Components/email-marketing-campaigns/email-marketing-campaigns.types';
import {MergeTagsSmartPreviewDropdown} from '@Components/merge-tags/merge-tags-smart-preview-dropdown/merge-tags-smart-preview-dropdown';
import type {MergeTagsSmartPreviewDropdownProps} from '@Components/merge-tags/merge-tags-smart-preview-dropdown/merge-tags-smart-preview-dropdown.types';
import type {EmailCampaignOptimalSendingProps} from '@Components/email-marketing-campaigns/components/email-campaign-optimal-sending-panel';
import {EmailCampaignOptimalSendingPanel} from '@Components/email-marketing-campaigns/components/email-campaign-optimal-sending-panel';
import type {EmailCampaignSchedulerBestTimeControlProps} from '@Components/email-marketing-campaigns/components/email-campaign-scheduler-best-time-control';
import {EmailCampaignSchedulerBestTimeControl} from '@Components/email-marketing-campaigns/components/email-campaign-scheduler-best-time-control';
import type {EmbeddedEditorAction} from '@Libraries/embedded-editor';
import {openEmailInEmbeddedEditor} from '@Libraries/embedded-editor';
import {openChangeEmailTemplateModal} from '@Modals/change-email-template-modal';
import type {EmailCampaignInputItemsProps} from '@Components/email-campaign-input-items/email-campaign-input-items.types';
import {EmailCampaignInputItems} from '@Components/email-campaign-input-items/email-campaign-input-items';
import type {AddContactToMailingListResponse, CreateMailingListResponse} from '@Components/mailing-list-wizard/mailing-list-wizard.types';
import EmailMakerShowMailingListsPanel from '@Components/email-maker-show-mailing-lists-panel/email-maker-show-mailing-lists-panel';
import {handleSmartMailingListsGaEvents} from '@Components/mailing-list-contacts/mailing-list-contacts-library';
import {ShareEmailInTeamToggler} from '@Components/share-email-in-team-toggler';
import {UserSaveConflictPopup} from '@Components/user-save-conflict-popup';
import {EditorType} from '@Utils/misc.util';
import {openUserSaveConflictModal} from '@Modals/user-save-conflict-modal';
import type {ShareInTeamBarState} from '@Components/share-in-team-toggle-bar/share-in-team-toggle-bar.types';
import type {EmailSignupForm} from '@/form-maker/grapesjs-form-maker/src/form-maker.types';
import {ShareSignupFormInTeamToggler} from '@Components/share-signup-form-in-team-toggler';

window.PMW.mergePMW(PMW, {
  openCalendarPopup(contentContainer: HTMLElement, opts: CalendarDateTimePickerProps = {}) {
    renderElement(contentContainer, <CalendarDateTimePicker {...opts} />);
  },
  initEmailCampaignInputItems(contentContainer: HTMLElement, props: EmailCampaignInputItemsProps) {
    renderElement(contentContainer, <EmailCampaignInputItems {...props} />);
  },
  initEmailCampaignSendingCta(contentContainer: HTMLElement, opts: EmailMakerSuccessCtaContainerProps) {
    renderElement(contentContainer, <EmailMakerSuccessCtaContainer {...opts} />);
  },
  initEmailCampaignOptimalTimeControls(contentContainer: HTMLElement, opts: EmailCampaignOptimalSendingProps) {
    renderElement(contentContainer, <EmailCampaignOptimalSendingPanel {...opts} />);
  },
  getBestTimeControlsForPicker(opts: EmailCampaignSchedulerBestTimeControlProps): ReactElement<any> {
    return <EmailCampaignSchedulerBestTimeControl {...opts} />;
  },
  publishEmailMarketingCampaignToSocialMedia(idEmailMarketingCampaign: string, onStart: VoidFunction, onFinish: VoidFunction) {
    void publishEmailCampaignToSocialMedia(idEmailMarketingCampaign, onStart, onFinish);
  },
  renderMailingListsPanel(
    contentContainer: HTMLElement,
    idEmailMarketingCampaign: string,
    onUpdateMailingListSelection: VoidFunction,
    onSpamInfoClick: VoidFunction,
    onCreateMailingListCallback: VoidFunction,
    onDeleteMailingListsCallback: VoidFunction,
    onEditMailingList: VoidFunction
  ) {
    renderElement(
      contentContainer,
      <EmailMakerShowMailingListsPanel
        onSpamInfoClick={onSpamInfoClick}
        onUpdateMailingListSelection={onUpdateMailingListSelection}
        onCreateMailingList={onCreateMailingListCallback}
        idCampaign={idEmailMarketingCampaign}
        onDeleteMailingList={onDeleteMailingListsCallback}
        onEditMailingList={onEditMailingList}
      />
    );
  },
  openAddMailingListModal(
    onAddAudienceSuccess: (sucessResponse: CreateMailingListResponse | AddContactToMailingListResponse) => void,
    mailingListId: number | undefined = undefined,
    idEmailMarketingCampaign: string | undefined = undefined
  ) {
    openAddMailingListModal({
      panelId: PANEL_ID,
      initialStep: AddMailingListStep.CHOOSE_UPLOAD_METHOD,
      onAddAudienceSuccess,
      mailingListId,
      idEmailMarketingCampaign,
    });
  },

  async openEmailEditorSocialMediaPublishingOptions(
    getEmailCampaignID: () => Promise<string>,
    type: EmailCampaignSendingOptionsPanelTypes,
    onClickBack: VoidFunction
  ): Promise<void> {
    window.PMW.closeModalDialog(window.PMW.emails.MORE_OPTIONS_DIALOG);
    window.PMW.setLoading(window.i18next.t('pmwjs_loading'));

    const campaignId = await getEmailCampaignID();

    window.PMW.setLoading(null);
    if (!campaignId) {
      return;
    }

    openEmailCampaignSendingOptionsModal({
      campaignId,
      type,
      showBackButton: true,
      onClickBack,
    });
  },

  openShareSignupFormInTeamToggler(component: HTMLElement | null, emailSignupForm: EmailSignupForm, onStateUpdate: (state: ShareInTeamBarState) => void, gaEvent = '', gaSource = ''): void {
    if (!component) {
      return;
    }

    renderElement(
      component,
      <Provider store={window.PMW.redux.store}>
        <ShareSignupFormInTeamToggler key={`form-${emailSignupForm.lastModifiedOn}`} signupForm={emailSignupForm} onStateUpdate={onStateUpdate} className={'signup-form-team-share-btn'} gaEvent={gaEvent} gaSource={gaSource} />
      </Provider>
    );
  },

  openShareEmailInTeamToggler(component: HTMLElement | null, emailCampaign: EmailCampaign, onStateUpdate: (state: ShareInTeamBarState) => void, gaEvent = '', gaSource = ''): void {
    if (!component) {
      return;
    }

    renderElement(
      component,
      <Provider store={window.PMW.redux.store}>
        <ShareEmailInTeamToggler key={`campaign-${emailCampaign.lastModified}`} campaign={emailCampaign} onStateUpdate={onStateUpdate} gaEvent={gaEvent} gaSource={gaSource} />
      </Provider>
    );
  },

  openUserSaveConflictPopup(
    component: HTMLElement | null,
    currentUserPictureURL: string,
    otherUserPictureURL: string,
    otherUserName: string,
    onAcceptYours: VoidFunction,
    onAcceptTheirs: VoidFunction,
    editorType: EditorType
  ): void {
    if (!component) {
      return;
    }

    renderElement(
      component,
      <Provider store={window.PMW.redux.store}>
        <UserSaveConflictPopup
          editorType={editorType}
          currentUserPictureURL={currentUserPictureURL}
          otherUserName={otherUserName}
          otherUserPictureURL={otherUserPictureURL}
          onAcceptYours={onAcceptYours}
          onAcceptTheirs={onAcceptTheirs}
        />
      </Provider>
    );
  },

  openUserSaveConflictModal(currentUserPictureURL: string, otherUserPictureURL: string, otherUserName: string, onAcceptYours: VoidFunction, onAcceptTheirs: VoidFunction): void {
    openUserSaveConflictModal({
      currentUserPictureURL,
      otherUserPictureURL,
      otherUserName,
      onAcceptYours,
      onAcceptTheirs,
      editorType: EditorType.EMAIL,
    });
  },

  initEmailEditorPublishingOptionsDropdown(component: HTMLElement | null, getEmailCampaign: () => Promise<EmailCampaign | undefined>): void {
    if (!component) {
      return;
    }
    const getPublishOptionsCTA = (): ReactElement<any> => {
      return <Icon icon="icon-more-h" className="spacing-m-l-2 hidden-for-mobile" size={IconSize.SIZE_ICON_20} shape={IconShape.SQUARE} type={IconType.GHOST} />;
    };

    renderElement(
      component,
      <Provider store={window.PMW.redux.store}>
        <EmailMakerPublishingOptionsPopup selector={getPublishOptionsCTA()} dropdownAlignment={ALIGNMENT_TYPE.BOTTOM_START} getEmailCampaign={getEmailCampaign} />
      </Provider>
    );
  },

  initMergeTagsTestEmailDropdown(
    contentContainer: HTMLElement | null,
    onConfirm: MergeTagsSmartPreviewDropdownProps['onConfirmed'],
    idEmailCmapign: string,
    tagsInCampaign: MergeTagsSmartPreviewDropdownProps['tagsInCampaign']
  ) {
    if (!contentContainer) {
      return;
    }

    renderElement(
      contentContainer,
      <Provider store={window.PMW.redux.store}>
        <MergeTagsSmartPreviewDropdown
          idEmailCampaign={idEmailCmapign}
          tagsInCampaign={tagsInCampaign}
          triggerButton={
            <Button
              icon="icon-envelope"
              iconPlacement={IconPlacement.LEFT}
              type={Type.PRIMARY}
              text={window.i18next.t('pmwjs_publishing_send_test_email')}
              isFullWidth
              size={Size.SMALL}
              customClasses="send-test-email js-send-test-email-btn"
              iconClassName="send-test-email-icon js-send-test-email-icon"
            />
          }
          confirmationButton={
            <Button
              icon="icon-arrow-right"
              iconPlacement={IconPlacement.RIGHT}
              type={Type.SECONDARY}
              text={window.i18next.t('pmwjs_next')}
              isFullWidth
              customClasses="spacing-m-t-2"
              size={Size.SMALL}
            />
          }
          onConfirmed={onConfirm}
        />
      </Provider>
    );
  },
  initMergeTagsPreviewDropdown(
    contentContainer: HTMLElement | null,
    onConfirm: MergeTagsSmartPreviewDropdownProps['onConfirmed'],
    idEmailCmapign: string,
    tagsInCampaign: MergeTagsSmartPreviewDropdownProps['tagsInCampaign']
  ) {
    if (!contentContainer) {
      return;
    }

    renderElement(
      contentContainer,
      <Provider store={window.PMW.redux.store}>
        <MergeTagsSmartPreviewDropdown
          idEmailCampaign={idEmailCmapign}
          tagsInCampaign={tagsInCampaign}
          triggerButton={
            <Button
              icon="icon-external-link"
              iconPlacement={IconPlacement.LEFT}
              type={Type.SECONDARY}
              text={window.i18next.t('pmwjs_publishing_preview_browser')}
              isFullWidth
              size={Size.SMALL}
            />
          }
          confirmationButton={
            <Button
              icon="icon-external-link"
              iconPlacement={IconPlacement.LEFT}
              type={Type.SECONDARY}
              text={window.i18next.t('pmwjs_publishing_preview_browser')}
              isFullWidth
              customClasses="spacing-m-t-2"
              size={Size.SMALL}
            />
          }
          onConfirmed={onConfirm}
        />
      </Provider>
    );
  },

  openEmbeddedChangeTemplateFlow(onSave: (templateId: string) => void, eventId = ''): void {
    openChangeEmailTemplateModal({
      onSave,
      eventId,
    });
  },

  trackSmartMailingListsIfNeeded(selectedMailingListIds: number[]): void {
    handleSmartMailingListsGaEvents(selectedMailingListIds);
  },

  openEmbeddedEmailEditor(emailId: string, onSave: (templateId: string) => void, editorAction: string, customNextButtonCaption: string) {
    const shouldHideEditorPreviewButton = true;
    const autoUpdateParamsString = '';
    openEmailInEmbeddedEditor(emailId, noop, onSave, noop, editorAction as EmbeddedEditorAction, customNextButtonCaption, autoUpdateParamsString, shouldHideEditorPreviewButton);
  },
});
